/**
 * Data layer for `advertiser-rbac`. Thin typed fetch wrappers over
 * /api/v1/advertiser-rbac. Raw `fetch()` is confined to this file per the
 * dash contract (D6).
 *
 * BE shape (see apps/api/src/routes/advertiser-rbac.ts):
 *   GET    /roles                     -> { roles: AdvertiserRoleRow[] }
 *   POST   /roles  { name, permissions } -> { role }
 *   DELETE /roles/:role_id            -> { ok, role_id }
 *   GET    /perms                     -> { perms: PermSlug[] }
 */

export type PermSlug =
	| "campaigns.read"
	| "campaigns.write"
	| "creatives.read"
	| "creatives.write"
	| "caps.write"
	| "conversions.approve"
	| "invoices.read";

export interface AdvertiserRoleRow {
	id: string;
	advertiser_id: string;
	name: string;
	permissions: PermSlug[];
	created_at: number;
	updated_at: number;
}

export interface ListRolesResponse {
	roles: AdvertiserRoleRow[];
}

export interface ListPermsResponse {
	perms: PermSlug[];
}

export interface CreateRoleBody {
	name: string;
	permissions: PermSlug[];
}

export interface CreateRoleResponse {
	role: AdvertiserRoleRow;
}

export interface DeleteRoleResponse {
	ok: boolean;
	role_id: string;
}

const BASE = "/api/v1/advertiser-rbac";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listRoles(): Promise<ListRolesResponse> {
	const res = await fetch(`${BASE}/roles`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListRolesResponse>(res);
}

export async function listPerms(): Promise<ListPermsResponse> {
	const res = await fetch(`${BASE}/perms`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPermsResponse>(res);
}

export async function createRole(
	body: CreateRoleBody,
): Promise<CreateRoleResponse> {
	const res = await fetch(`${BASE}/roles`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateRoleResponse>(res);
}

export async function deleteRole(roleId: string): Promise<DeleteRoleResponse> {
	const res = await fetch(
		`${BASE}/roles/${encodeURIComponent(roleId)}`,
		{
			method: "DELETE",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<DeleteRoleResponse>(res);
}

export const advertiserRbacQueryKeys = {
	all: ["advertiser-rbac"] as const,
	roles: () => [...advertiserRbacQueryKeys.all, "roles"] as const,
	role: (id: string) => [...advertiserRbacQueryKeys.all, "roles", id] as const,
	perms: () => [...advertiserRbacQueryKeys.all, "perms"] as const,
};
